import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Blase } from "./Blase";
import type { SatzTeil } from "./blasenForm";
import { darf } from "./darf";
import { useWelt } from "./einstellungen";
import { Geist } from "./Geist";
import { useGeistOptional } from "./geistKontext";
import { SprecherKontext } from "./kontext";

/** Geist-Szene (Sprecher-Regel): links sein Platz (Kopf lebhaft 132 x 172, Geist auf 43 % der Höhe; ruhig 108 x 136,
 *  46 %; Akten 76 x 92 beziehungsweise 80 x 96), rechts die Blase mit der Spitze zu ihm. Darf er nicht sprechen
 *  (`darf("satzKopf")` beziehungsweise `satzAkten`), steht statt der Blase die Kopfzeile. Fliegt er weg, nimmt er den
 *  Satz mit: Platz und Blase bleiben leer. */
export function GeistSzene(props: {
  art?: "kopf" | "akten";
  satz: SatzTeil[] | null;
  /** Zeile ohne Ich-Stimme statt der Blase (Inhalt in Web-2) */
  kopfzeile?: ReactNode;
  erklaert?: boolean;
  /** Sein Platz (Ausgang und Ziel der Flüge, Vorgabe für den Kopf); nur diese Szene wird leer, wenn er unterwegs ist */
  heim?: boolean;
  kennung?: string;
  /** unter dem Satz in der Blase, etwa der Knopf „Zeig es mir“ */
  children?: ReactNode;
  /** zweite Blase nach dem Satz (Präsenz-Hinweis, E13); nur, wenn er spricht */
  nachsatz?: ReactNode;
}) {
  const [welt] = useWelt();
  const ebene = useGeistOptional();
  const platz = useRef<HTMLSpanElement>(null);
  const art = props.art ?? "kopf";
  const heim = props.heim ?? art === "kopf";
  const melden = heim ? ebene?.platzMelden : undefined;
  useLayoutEffect(() => {
    melden?.(platz.current);
    return () => melden?.(null);
  }, [melden]);
  // W82 Web-6a-N1 (WN-39, D-1148): `data-blase-weg` folgt `data-unterwegs`, sobald der Ausgang der Kopfblase vorbei ist (seine Dauer
  // gelesen am Element). Bei reduzierter Bewegung verbirgt erst das die Blase (welt.css), so blendet sie 120 ms nur über die
  // Deckkraft aus, ohne zweiten Übergang (die Bestandsprobe w82-bausteine:103 nimmt nur den Deckkraft-Übergang aus)
  const unterwegs = heim && Boolean(ebene?.unterwegs);
  const [flug, setFlug] = useState({ unterwegs, nr: 0 });
  if (flug.unterwegs !== unterwegs) setFlug({ unterwegs, nr: flug.nr + (unterwegs ? 1 : 0) });
  const [wegNr, setWegNr] = useState(-1);
  const reden = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!flug.unterwegs) return;
    const b = reden.current?.querySelector(".blase");
    const ms = b ? Math.max(0, ...getComputedStyle(b).transitionDuration.split(",").map((d, i) => (parseFloat(d) + (parseFloat(getComputedStyle(b).transitionDelay.split(",")[i] ?? "0") || 0)) * 1000 || 0)) : 0;
    const takt = setTimeout(() => setWegNr(flug.nr), ms);
    return () => clearTimeout(takt);
  }, [flug]);
  const spricht = props.satz !== null && darf(art === "akten" ? "satzAkten" : "satzKopf", { fassung: welt.fassung, praesenz: welt.praesenz, erklaert: props.erklaert ?? false });
  return (
    <SprecherKontext.Provider value="szene">
      <div className="geist-szene" data-geist-szene="" data-art={art} data-unterwegs={unterwegs ? "" : undefined} data-blase-weg={unterwegs && wegNr === flug.nr ? "" : undefined} data-testid={props.kennung}>
        <span ref={platz} className="geist-platz" data-geist-platz="" aria-hidden="true">
          <Geist groesse={art === "akten" ? 54 : undefined} />
        </span>
        {spricht && props.satz ? (
          <div ref={reden} className="geist-reden">
            <Blase form="kopf" satz={props.satz} stufe={art === "akten" ? "w-satz-akten" : undefined}>
              {props.children}
            </Blase>
            {props.nachsatz}
          </div>
        ) : (
          <p className="kopf-zeile w-kopf-zeile">{props.kopfzeile}</p>
        )}
      </div>
    </SprecherKontext.Provider>
  );
}
